import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/auth/AuthContext";
import ProtectedRoute from "@/auth/ProtectedRoute";
import Layout from "@/components/Layout";

import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import VehicleDetails from "@/pages/VehicleDetails";
import Booking from "@/pages/Booking";
import BookingComplete from "@/pages/BookingComplete";
import Login from "@/pages/Login";
import DashboardIndex from "@/pages/DashboardIndex";
import RenterDashboard from "@/pages/RenterDashboard";
import NewVehicle from "@/pages/NewVehicle";
import EditVehicle from "@/pages/EditVehicle";
import EditParking from "@/pages/EditParking";
import AdminDashboard from "@/pages/AdminDashboard";
import Loyalty from "@/pages/Loyalty";
import Notifications from "@/pages/Notifications";
import HostHub from "@/pages/HostHub";
import Invest from "@/pages/Invest";
import InvestDetails from "@/pages/InvestDetails";
import FindParking from "@/pages/FindParking";
import ParkingDetails from "@/pages/ParkingDetails";
import ParkingComplete from "@/pages/ParkingComplete";
import NewParking from "@/pages/NewParking";
import ProfileBookings from "@/pages/ProfileBookings";
import ProfileRented from "@/pages/ProfileRented";
import ProfileInvested from "@/pages/ProfileInvested";
import ProfileTransactions from "@/pages/ProfileTransactions";
import ProfileMessages from "@/pages/ProfileMessages";
import ProfileEsims from "@/pages/ProfileEsims";
import Help from "@/pages/Help";
import Safety from "@/pages/Safety";
import Terms from "@/pages/Terms";
import Cancellation from "@/pages/Cancellation";
import ProfileSettings from "@/pages/ProfileSettings";
import ProfileSupport from "@/pages/ProfileSupport";
import ExternalDetails from "@/pages/ExternalDetails";
import ExternalBookingComplete from "@/pages/ExternalBookingComplete";
import MobileInspect from "@/pages/MobileInspect";
import DesignSystem from "@/pages/DesignSystem";
import ScrollToTop from "@/components/ScrollToTop";
import { FRACTIONAL_ENABLED } from "@/lib/featureFlags";

function AppRouter() {
  return (
    <Layout>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/vehicles/:id" element={<VehicleDetails />} />
        <Route path="/host" element={<HostHub />} />
        <Route path="/invest" element={FRACTIONAL_ENABLED ? <Invest /> : <Navigate to="/browse" replace />} />
        <Route path="/invest/:id" element={FRACTIONAL_ENABLED ? <InvestDetails /> : <Navigate to="/browse" replace />} />
        <Route path="/parking" element={<FindParking />} />
        <Route path="/parking/new" element={<ProtectedRoute><NewParking /></ProtectedRoute>} />
        <Route path="/parking/edit/:id" element={<ProtectedRoute><EditParking /></ProtectedRoute>} />
        <Route path="/parking/:id" element={<ParkingDetails />} />
        <Route path="/parking/:id/complete" element={<ProtectedRoute><ParkingComplete /></ProtectedRoute>} />
        <Route path="/dashboard/parking" element={<ProtectedRoute><Navigate to="/host?tab=parking" replace /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfileBookings /></ProtectedRoute>} />
        <Route path="/profile/bookings" element={<ProtectedRoute><ProfileBookings /></ProtectedRoute>} />
        <Route path="/profile/rented" element={<ProtectedRoute><ProfileRented /></ProtectedRoute>} />
        <Route path="/profile/parking" element={<ProtectedRoute><Navigate to="/profile/bookings?tab=parking" replace /></ProtectedRoute>} />
        <Route path="/profile/invested" element={<ProtectedRoute>{FRACTIONAL_ENABLED ? <ProfileInvested /> : <Navigate to="/profile" replace />}</ProtectedRoute>} />
        <Route path="/profile/transactions" element={<ProtectedRoute><ProfileTransactions /></ProtectedRoute>} />
        <Route path="/profile/messages" element={<ProtectedRoute><ProfileMessages /></ProtectedRoute>} />
        <Route path="/profile/support" element={<ProtectedRoute><ProfileSupport /></ProtectedRoute>} />
        <Route path="/profile/esim" element={<ProtectedRoute><ProfileEsims /></ProtectedRoute>} />
        <Route path="/login" element={<Login />} />
        <Route path="/help" element={<Help />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cancellation" element={<Cancellation />} />
        <Route path="/profile/settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
        <Route path="/profile/loyalty" element={<Navigate to="/loyalty" replace />} />
        <Route path="/external/:id" element={<ExternalDetails />} />
        <Route path="/external/:id/complete" element={<ProtectedRoute><ExternalBookingComplete /></ProtectedRoute>} />
        <Route path="/inspect/:token" element={<MobileInspect />} />

        <Route path="/book/:id" element={<ProtectedRoute><Booking /></ProtectedRoute>} />
        <Route path="/book/:id/complete" element={<ProtectedRoute><BookingComplete /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardIndex /></ProtectedRoute>} />
        <Route path="/dashboard/renter" element={<ProtectedRoute><RenterDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/owner" element={<ProtectedRoute><Navigate to="/host?tab=cars" replace /></ProtectedRoute>} />
        <Route path="/dashboard/owner/new" element={<ProtectedRoute><NewVehicle /></ProtectedRoute>} />
        <Route path="/dashboard/owner/edit/:id" element={<ProtectedRoute><EditVehicle /></ProtectedRoute>} />
        <Route path="/dashboard/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
        <Route path="/loyalty" element={<ProtectedRoute><Loyalty /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        {process.env.NODE_ENV === "development" && (
          <Route path="/design-system" element={<DesignSystem />} />
        )}
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
